import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider, useAuth } from '@/context/AuthContext'
import { ServiceHealthProvider } from '@/context/ServiceHealthContext'
import { AnimatePresence } from 'framer-motion'
import PageTransition from '@/components/ui/PageTransition'
import PortalLayout from '@/components/layout/PortalLayout'
import LoadingScreen from '@/components/LoadingScreen'
import ErrorBoundary from '@/components/ui/ErrorBoundary'
import { NotificationProvider } from '@/context/NotificationContext'
import { SipProvider } from '@/context/SipContext'
import GlobalToastContainer from '@/components/ui/GlobalToastContainer'

// Singleton QueryClient — staleTime 30s, retries 1
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime:          30_000,
      retry:              1,
      refetchOnWindowFocus: false,
    },
  },
})

// ── Route-based code splitting — all pages lazy loaded ─────────────────────
// This reduces the initial JS bundle by ~60% (1.49MB → ~250KB first load)
const LoginPage            = lazy(() => import('@/pages/LoginPage'))
const ResetPasswordPage    = lazy(() => import('@/pages/ResetPasswordPage'))
const AgentDashboardPage   = lazy(() => import('@/pages/AgentDashboardPage'))
const AgentsPage           = lazy(() => import('@/pages/AgentsPage'))
const LeadsPage            = lazy(() => import('@/pages/LeadsPage'))
const CallDialerPage       = lazy(() => import('@/pages/CallDialerPage'))
const SmsPage              = lazy(() => import('@/pages/SmsPage'))
const ChatPage             = lazy(() => import('@/pages/ChatPage'))
const TeamChatPage         = lazy(() => import('@/pages/TeamChatPage'))
const ReportsPage          = lazy(() => import('@/pages/ReportsPage'))
const ShiftsPage           = lazy(() => import('@/pages/ShiftsPage'))
const SettingsPage         = lazy(() => import('@/pages/SettingsPage'))
const WarRoomPage          = lazy(() => import('@/pages/WarRoomPage'))
const PerformanceBoardPage = lazy(() => import('@/pages/PerformanceBoardPage'))
const PlaceholderPage      = lazy(() => import('@/pages/PlaceholderPage'))
const SequencesPage        = lazy(() => import('@/pages/SequencesPage'))
const SalesAnalyticsPage   = lazy(() => import('@/pages/SalesAnalyticsPage'))
const LeaderboardPage      = lazy(() => import('@/pages/LeaderboardPage'))
const ScriptLibraryPage    = lazy(() => import('@/pages/ScriptLibraryPage'))
const TrainingPage         = lazy(() => import('@/pages/TrainingPage'))
const HrPage               = lazy(() => import('@/pages/HrPage'))
const ProfilePage          = lazy(() => import('@/pages/ProfilePage'))

// Suspense + ErrorBoundary per page — isolated crashes, never full app whiteout
function PageSuspense({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingScreen />}>{children}</Suspense>
    </ErrorBoundary>
  )
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()
  if (isLoading) return <LoadingScreen />
  if (!isAuthenticated) return <Navigate to="/login" replace />
  return <PortalLayout>{children}</PortalLayout>
}

// Performance board wraps in layout-free fullscreen — no sidebar/topbar
function BoardRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()
  if (isLoading) return <LoadingScreen />
  if (!isAuthenticated) return <Navigate to="/login" replace />
  return <>{children}</>
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()
  if (isLoading) return <LoadingScreen />
  if (isAuthenticated) return <Navigate to="/dashboard" replace />
  return <>{children}</>
}

function AppRoutes() {
  const location = useLocation()

  return (
    <AnimatePresence mode="wait" initial={false}>
      <Routes location={location} key={location.pathname}>
        {/* Public */}
        <Route path="/login" element={
          <PublicRoute><PageSuspense><LoginPage /></PageSuspense></PublicRoute>
        } />
        <Route path="/reset-password" element={
          <PageSuspense><ResetPasswordPage /></PageSuspense>
        } />

        {/* Protected — wrapped in PortalLayout */}
        <Route path="/dashboard" element={
          <ProtectedRoute><PageSuspense><PageTransition><AgentDashboardPage /></PageTransition></PageSuspense></ProtectedRoute>
        } />
        <Route path="/agents" element={
          <ProtectedRoute><PageSuspense><PageTransition><AgentsPage /></PageTransition></PageSuspense></ProtectedRoute>
        } />
        <Route path="/leads" element={
          <ProtectedRoute><PageSuspense><PageTransition><LeadsPage /></PageTransition></PageSuspense></ProtectedRoute>
        } />
        <Route path="/calls" element={
          <ProtectedRoute><PageSuspense><PageTransition><CallDialerPage /></PageTransition></PageSuspense></ProtectedRoute>
        } />
        <Route path="/sms" element={
          <ProtectedRoute><PageSuspense><PageTransition><SmsPage /></PageTransition></PageSuspense></ProtectedRoute>
        } />
        <Route path="/chat" element={
          <ProtectedRoute><PageSuspense><PageTransition><ChatPage /></PageTransition></PageSuspense></ProtectedRoute>
        } />
        <Route path="/team-chat" element={
          <ProtectedRoute><PageSuspense><PageTransition><TeamChatPage /></PageTransition></PageSuspense></ProtectedRoute>
        } />
        <Route path="/reports" element={
          <ProtectedRoute><PageSuspense><PageTransition><ReportsPage /></PageTransition></PageSuspense></ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute><PageSuspense><PageTransition><SettingsPage /></PageTransition></PageSuspense></ProtectedRoute>
        } />
        <Route path="/shifts" element={
          <ProtectedRoute><PageSuspense><PageTransition><ShiftsPage /></PageTransition></PageSuspense></ProtectedRoute>
        } />
        <Route path="/war-room" element={
          <ProtectedRoute><PageSuspense><PageTransition><WarRoomPage /></PageTransition></PageSuspense></ProtectedRoute>
        } />

        {/* Performance board — fullscreen, no sidebar */}
        <Route path="/board" element={
          <BoardRoute><PageSuspense><PerformanceBoardPage /></PageSuspense></BoardRoute>
        } />

        {/* Phase 1 — complete */}
        <Route path="/sequences" element={
          <ProtectedRoute><PageSuspense><PageTransition><SequencesPage /></PageTransition></PageSuspense></ProtectedRoute>
        } />
        <Route path="/sales" element={
          <ProtectedRoute><PageSuspense><PageTransition><SalesAnalyticsPage /></PageTransition></PageSuspense></ProtectedRoute>
        } />
        <Route path="/leaderboard" element={
          <ProtectedRoute><PageSuspense><PageTransition><LeaderboardPage /></PageTransition></PageSuspense></ProtectedRoute>
        } />
        <Route path="/scripts" element={
          <ProtectedRoute><PageSuspense><PageTransition><ScriptLibraryPage /></PageTransition></PageSuspense></ProtectedRoute>
        } />
        <Route path="/training" element={
          <ProtectedRoute><PageSuspense><PageTransition><TrainingPage /></PageTransition></PageSuspense></ProtectedRoute>
        } />
        <Route path="/hr" element={
          <ProtectedRoute><PageSuspense><PageTransition><HrPage /></PageTransition></PageSuspense></ProtectedRoute>
        } />
        <Route path="/profile" element={
          <ProtectedRoute><PageSuspense><PageTransition><ProfilePage /></PageTransition></PageSuspense></ProtectedRoute>
        } />

        {/* Default */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </AnimatePresence>
  )
}

// QueryClientProvider is outermost — all auth, routing, and data providers nest inside it.
// Auth → ServiceHealth order is fully preserved.
export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <ServiceHealthProvider>
            <NotificationProvider>
              <SipProvider>
                <AppRoutes />
                <GlobalToastContainer />
                <Toaster
                  position="bottom-right"
                  toastOptions={{
                    style: {
                      background: 'var(--bg-elevated)',
                      color: 'var(--text-primary)',
                      border: '1px solid var(--border-default)',
                      fontFamily: 'var(--font-body)',
                      fontSize: '0.875rem',
                    },
                    success: {
                      iconTheme: { primary: 'var(--status-success)', secondary: 'var(--bg-elevated)' },
                    },
                    error: {
                      iconTheme: { primary: 'var(--status-error)', secondary: 'var(--bg-elevated)' },
                    },
                  }}
                />
              </SipProvider>
            </NotificationProvider>
          </ServiceHealthProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  )
}
